import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import './styles/App.css';

// Contexts
import { AuthProvider } from './contexts/AuthContext';
import { CounterProvider } from './contexts/counterContext';
import { NotificationProvider } from './contexts/NotificationContext';

// Components (Global)
import NavBar from './shared/components/Navbar';
import Footer from './shared/components/Footer';
import LocationPopup from './shared/components/LocationPopup';
import ScrollToTop from './shared/components/ScrollToTop';
import Loading from './shared/components/Loading';

// Lazy Loaded Components
const HomePage = lazy(() => import('./user/pages/Home'));
const ProductDetail = lazy(() => import('./user/pages/ProductDetail'));
const Cart = lazy(() => import('./user/pages/Cart'));
const Wishlist = lazy(() => import('./user/pages/Wishlist'));
const LoginPage = lazy(() => import('./auth/pages/Login'));
const RegisterPage = lazy(() => import('./auth/pages/Signup'));
const AboutCeler = lazy(() => import('./user/pages/About'));
const ChatList = lazy(() => import('./user/pages/ChatList'));
const MessageChat = lazy(() => import('./user/pages/Chat'));
const AllProducts = lazy(() => import('./user/pages/AllProducts'));
const HandleCategories = lazy(() => import('./user/pages/CategoryProducts'));
const UserProfile = lazy(() => import('./user/pages/Profile'));
const Feed = lazy(() => import('./user/pages/Feed'));
const PostDetail = lazy(() => import('./user/pages/PostDetail'));
const Checkout = lazy(() => import('./user/pages/Checkout'));
const MyOrders = lazy(() => import('./user/pages/Orders'));
const HowItWorks = lazy(() => import('./user/pages/HowItWorks'));
const AdminPanel = lazy(() => import('./admin/pages/AdminDashboard'));
const SellerDashboard = lazy(() => import('./seller/pages/SellerDashboard'));
import AdminRoute from './auth/guards/AdminRoute';

function AppContent() {
  const location = useLocation();
  const [showLocationPopup, setShowLocationPopup] = React.useState(false);

  React.useEffect(() => {
    const savedPincode = localStorage.getItem("delivery_pincode");
    if (!savedPincode) {
      setShowLocationPopup(true);
    }
  }, []);

  const isAdminRoute = location.pathname.startsWith('/admin');

  const routes = (
    <Suspense fallback={<Loading />}>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/home" element={<HomePage />} />
        <Route path="/feed" element={<Feed />} />
        <Route path="/post/:id" element={<PostDetail />} />
        <Route path="/products" element={<AllProducts />} />
        <Route path="/products/category/:id" element={<HandleCategories />} />
        <Route path="/product/:id" element={<ProductDetail />} />
        <Route path="/cart" element={<Cart />} />
        <Route path="/wishlist" element={<Wishlist />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<RegisterPage />} />
        <Route path="/about" element={<AboutCeler />} />
        <Route path="/chats" element={<ChatList />} />
        <Route path="/message/:sellerName/:sellerId" element={<MessageChat />} />
        <Route path="/profile" element={<UserProfile />} />
        <Route path="/checkout" element={<Checkout />} />
        <Route path="/orders" element={<MyOrders />} />
        <Route path="/how-it-works" element={<HowItWorks />} />
        <Route path="/account" element={<UserProfile />} />
        {/* Seller Dashboard */}
        <Route path="/seller-dashboard" element={<SellerDashboard />} />
        {/* Admin Routes */}
        <Route element={<AdminRoute />}>
          <Route path="/admin" element={<AdminPanel />} />
        </Route>
      </Routes>
    </Suspense>
  );

  if (isAdminRoute) {
    return (
      <>
        <ScrollToTop />
        {routes}
      </>
    );
  }

  return (
    <div className="App">
      <ScrollToTop />
      <NavBar onLocationClick={() => setShowLocationPopup(true)} />
      <LocationPopup show={showLocationPopup} onHide={() => setShowLocationPopup(false)} />
      {routes}
      <Footer />
    </div>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <NotificationProvider>
          <CounterProvider>
            <AppContent />
          </CounterProvider>
        </NotificationProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;